/**
 * Custom User Module GraphQL query and mutation helpers.
 *
 * Uses "Custom" prefixed GraphQL operation names to avoid conflicts with
 * auto-generated operations in mutations.ts / queries.ts.
 * Follows the pattern established by pmQueries.ts and trainingQueries.ts.
 */
import { authClient } from "../utils/amplifyClient";
import { ModelSortDirection } from "../types/schema";

/** null = unknown; set after first skill-assignment query/mutation that tries evaluationHistory */
let skillAssignmentHistorySupported: boolean | null = null;

// ---------------------------------------------------------------------------
// Custom query strings — Classifications
// ---------------------------------------------------------------------------

const classificationsByCompanyIdQuery = /* GraphQL */ `
  query CustomClassificationsByCompanyId(
    $companyId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelClassificationFilterInput
    $limit: Int
    $nextToken: String
  ) {
    classificationsByCompanyId(
      companyId: $companyId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        companyId
        name
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom query strings — Skill Categories
// ---------------------------------------------------------------------------

const skillCategoriesByCompanyIdQuery = /* GraphQL */ `
  query CustomSkillCategoriesByCompanyId(
    $companyId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillCategoryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillCategoriesByCompanyId(
      companyId: $companyId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        classificationId
        companyId
        name
        allowMachineNumber
        equipmentCategoryId
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const skillCategoriesByClassificationIdQuery = /* GraphQL */ `
  query CustomSkillCategoriesByClassificationId(
    $classificationId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillCategoryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillCategoriesByClassificationId(
      classificationId: $classificationId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        classificationId
        companyId
        name
        allowMachineNumber
        equipmentCategoryId
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom query strings — Skill Tasks
// ---------------------------------------------------------------------------

const skillTasksByCompanyIdQuery = /* GraphQL */ `
  query CustomSkillTasksByCompanyId(
    $companyId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillTaskFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillTasksByCompanyId(
      companyId: $companyId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        categoryId
        companyId
        name
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const skillTasksByCategoryIdQuery = /* GraphQL */ `
  query CustomSkillTasksByCategoryId(
    $categoryId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillTaskFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillTasksByCategoryId(
      categoryId: $categoryId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        categoryId
        companyId
        name
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom query strings — Sub-Categories
// ---------------------------------------------------------------------------

const subCategoriesByCompanyIdQuery = /* GraphQL */ `
  query CustomSubCategoriesByCompanyId(
    $companyId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSubCategoryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    subCategoriesByCompanyId(
      companyId: $companyId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        categoryId
        companyId
        name
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const subCategoriesByCategoryIdQuery = /* GraphQL */ `
  query CustomSubCategoriesByCategoryId(
    $categoryId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSubCategoryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    subCategoriesByCategoryId(
      categoryId: $categoryId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        categoryId
        companyId
        name
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom query strings — Skill Levels
// ---------------------------------------------------------------------------

const skillLevelsByCompanyIdQuery = /* GraphQL */ `
  query CustomSkillLevelsByCompanyId(
    $companyId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillLevelFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillLevelsByCompanyId(
      companyId: $companyId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        companyId
        name
        description
        color
        sortOrder
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom query strings — Skill Assignments
// ---------------------------------------------------------------------------

const skillAssignmentsByUserIdQuery = /* GraphQL */ `
  query CustomSkillAssignmentsByUserId(
    $userId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillAssignmentFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillAssignmentsByUserId(
      userId: $userId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userId
        taskId
        companyId
        subCategoryId
        skillLevel
        evaluatedBy
        evaluatedByName
        evaluationDate
        evaluationHistory
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const skillAssignmentsByUserIdQueryLegacy = /* GraphQL */ `
  query CustomSkillAssignmentsByUserIdLegacy(
    $userId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillAssignmentFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillAssignmentsByUserId(
      userId: $userId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userId
        taskId
        companyId
        subCategoryId
        skillLevel
        evaluatedBy
        evaluatedByName
        evaluationDate
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const skillAssignmentsByCompanyIdQuery = /* GraphQL */ `
  query CustomSkillAssignmentsByCompanyId(
    $companyId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillAssignmentFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillAssignmentsByCompanyId(
      companyId: $companyId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userId
        taskId
        companyId
        subCategoryId
        skillLevel
        evaluatedBy
        evaluatedByName
        evaluationDate
        evaluationHistory
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const skillAssignmentsByCompanyIdQueryLegacy = /* GraphQL */ `
  query CustomSkillAssignmentsByCompanyIdLegacy(
    $companyId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelSkillAssignmentFilterInput
    $limit: Int
    $nextToken: String
  ) {
    skillAssignmentsByCompanyId(
      companyId: $companyId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userId
        taskId
        companyId
        subCategoryId
        skillLevel
        evaluatedBy
        evaluatedByName
        evaluationDate
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom query strings — User Files
// ---------------------------------------------------------------------------

const userFilesByUserIdQuery = /* GraphQL */ `
  query CustomUserFilesByUserId(
    $userId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelUserFileFilterInput
    $limit: Int
    $nextToken: String
  ) {
    userFilesByUserId(
      userId: $userId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userId
        companyId
        fileName
        s3Path
        fileSize
        uploadedBy
        uploadedByName
        uploadedAt
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const userFilesByCompanyIdQuery = /* GraphQL */ `
  query CustomUserFilesByCompanyId(
    $companyId: String!
    $sortDirection: ModelSortDirection
    $filter: ModelUserFileFilterInput
    $limit: Int
    $nextToken: String
  ) {
    userFilesByCompanyId(
      companyId: $companyId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userId
        companyId
        fileName
        s3Path
        fileSize
        uploadedBy
        uploadedByName
        uploadedAt
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom query strings — Change Log Entries
// ---------------------------------------------------------------------------

const changeLogEntriesByUserIdQuery = /* GraphQL */ `
  query CustomChangeLogEntriesByUserIdAndCreatedAt(
    $userId: String!
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelChangeLogEntryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    changeLogEntriesByUserIdAndCreatedAt(
      userId: $userId
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userId
        companyId
        fieldName
        oldValue
        newValue
        description
        changedBy
        changedByName
        createdAt
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom mutation strings — Classification
// ---------------------------------------------------------------------------

const createClassificationMutation = /* GraphQL */ `
  mutation CustomCreateClassification($input: CreateClassificationInput!) {
    createClassification(input: $input) {
      id
      companyId
      name
      createdAt
      updatedAt
    }
  }
`;

const updateClassificationMutation = /* GraphQL */ `
  mutation CustomUpdateClassification($input: UpdateClassificationInput!) {
    updateClassification(input: $input) {
      id
      companyId
      name
      createdAt
      updatedAt
    }
  }
`;

const deleteClassificationMutation = /* GraphQL */ `
  mutation CustomDeleteClassification($input: DeleteClassificationInput!) {
    deleteClassification(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom mutation strings — SkillCategory
// ---------------------------------------------------------------------------

const createSkillCategoryMutation = /* GraphQL */ `
  mutation CustomCreateSkillCategory($input: CreateSkillCategoryInput!) {
    createSkillCategory(input: $input) {
      id
      classificationId
      companyId
      name
      allowMachineNumber
      equipmentCategoryId
      createdAt
      updatedAt
    }
  }
`;

const updateSkillCategoryMutation = /* GraphQL */ `
  mutation CustomUpdateSkillCategory($input: UpdateSkillCategoryInput!) {
    updateSkillCategory(input: $input) {
      id
      classificationId
      companyId
      name
      allowMachineNumber
      equipmentCategoryId
      createdAt
      updatedAt
    }
  }
`;

const deleteSkillCategoryMutation = /* GraphQL */ `
  mutation CustomDeleteSkillCategory($input: DeleteSkillCategoryInput!) {
    deleteSkillCategory(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom mutation strings — SkillTask
// ---------------------------------------------------------------------------

const createSkillTaskMutation = /* GraphQL */ `
  mutation CustomCreateSkillTask($input: CreateSkillTaskInput!) {
    createSkillTask(input: $input) {
      id
      categoryId
      companyId
      name
      createdAt
      updatedAt
    }
  }
`;

const updateSkillTaskMutation = /* GraphQL */ `
  mutation CustomUpdateSkillTask($input: UpdateSkillTaskInput!) {
    updateSkillTask(input: $input) {
      id
      categoryId
      companyId
      name
      createdAt
      updatedAt
    }
  }
`;

const deleteSkillTaskMutation = /* GraphQL */ `
  mutation CustomDeleteSkillTask($input: DeleteSkillTaskInput!) {
    deleteSkillTask(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom mutation strings — SubCategory
// ---------------------------------------------------------------------------

const createSubCategoryMutation = /* GraphQL */ `
  mutation CustomCreateSubCategory($input: CreateSubCategoryInput!) {
    createSubCategory(input: $input) {
      id
      categoryId
      companyId
      name
      createdAt
      updatedAt
    }
  }
`;

const updateSubCategoryMutation = /* GraphQL */ `
  mutation CustomUpdateSubCategory($input: UpdateSubCategoryInput!) {
    updateSubCategory(input: $input) {
      id
      categoryId
      companyId
      name
      createdAt
      updatedAt
    }
  }
`;

const deleteSubCategoryMutation = /* GraphQL */ `
  mutation CustomDeleteSubCategory($input: DeleteSubCategoryInput!) {
    deleteSubCategory(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom mutation strings — SkillLevel
// ---------------------------------------------------------------------------

const createSkillLevelMutation = /* GraphQL */ `
  mutation CustomCreateSkillLevel($input: CreateSkillLevelInput!) {
    createSkillLevel(input: $input) {
      id
      companyId
      name
      description
      color
      sortOrder
      createdAt
      updatedAt
    }
  }
`;

const updateSkillLevelMutation = /* GraphQL */ `
  mutation CustomUpdateSkillLevel($input: UpdateSkillLevelInput!) {
    updateSkillLevel(input: $input) {
      id
      companyId
      name
      description
      color
      sortOrder
      createdAt
      updatedAt
    }
  }
`;

const deleteSkillLevelMutation = /* GraphQL */ `
  mutation CustomDeleteSkillLevel($input: DeleteSkillLevelInput!) {
    deleteSkillLevel(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom mutation strings — SkillAssignment
// ---------------------------------------------------------------------------

const createSkillAssignmentMutation = /* GraphQL */ `
  mutation CustomCreateSkillAssignment($input: CreateSkillAssignmentInput!) {
    createSkillAssignment(input: $input) {
      id
      userId
      taskId
      companyId
      subCategoryId
      skillLevel
      evaluatedBy
      evaluatedByName
      evaluationDate
      createdAt
      updatedAt
    }
  }
`;

const updateSkillAssignmentMutation = /* GraphQL */ `
  mutation CustomUpdateSkillAssignment($input: UpdateSkillAssignmentInput!) {
    updateSkillAssignment(input: $input) {
      id
      userId
      taskId
      companyId
      subCategoryId
      skillLevel
      evaluatedBy
      evaluatedByName
      evaluationDate
      createdAt
      updatedAt
    }
  }
`;

const deleteSkillAssignmentMutation = /* GraphQL */ `
  mutation CustomDeleteSkillAssignment($input: DeleteSkillAssignmentInput!) {
    deleteSkillAssignment(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom mutation strings — UserFile
// ---------------------------------------------------------------------------

const createUserFileMutation = /* GraphQL */ `
  mutation CustomCreateUserFile($input: CreateUserFileInput!) {
    createUserFile(input: $input) {
      id
      userId
      companyId
      fileName
      s3Path
      fileSize
      uploadedBy
      uploadedByName
      uploadedAt
      createdAt
      updatedAt
    }
  }
`;

const deleteUserFileMutation = /* GraphQL */ `
  mutation CustomDeleteUserFile($input: DeleteUserFileInput!) {
    deleteUserFile(input: $input) {
      id
    }
  }
`;

const updateUserFileMutation = /* GraphQL */ `
  mutation CustomUpdateUserFile($input: UpdateUserFileInput!) {
    updateUserFile(input: $input) {
      id
      userId
      companyId
      fileName
      s3Path
      fileSize
      uploadedBy
      uploadedByName
      uploadedAt
      createdAt
      updatedAt
    }
  }
`;

// ---------------------------------------------------------------------------
// Custom mutation strings — ChangeLogEntry
// ---------------------------------------------------------------------------

const createChangeLogEntryMutation = /* GraphQL */ `
  mutation CustomCreateChangeLogEntry($input: CreateChangeLogEntryInput!) {
    createChangeLogEntry(input: $input) {
      id
      userId
      companyId
      fieldName
      oldValue
      newValue
      description
      changedBy
      changedByName
      createdAt
    }
  }
`;

// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------

/** Fetch all classifications for a company, handling pagination. */
export async function classificationsByCompanyId(companyId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: classificationsByCompanyIdQuery,
      variables: { companyId, limit: 1000, nextToken: token },
    });
    const data = result.data.classificationsByCompanyId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch all skill categories for a company, handling pagination. */
export async function skillCategoriesByCompanyId(companyId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: skillCategoriesByCompanyIdQuery,
      variables: { companyId, limit: 1000, nextToken: token },
    });
    const data = result.data.skillCategoriesByCompanyId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch skill categories by classification ID, handling pagination. */
export async function skillCategoriesByClassificationId(classificationId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: skillCategoriesByClassificationIdQuery,
      variables: { classificationId, limit: 1000, nextToken: token },
    });
    const data = result.data.skillCategoriesByClassificationId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch all skill tasks for a company, handling pagination. */
export async function skillTasksByCompanyId(companyId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: skillTasksByCompanyIdQuery,
      variables: { companyId, limit: 1000, nextToken: token },
    });
    const data = result.data.skillTasksByCompanyId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch skill tasks by category ID, handling pagination. */
export async function skillTasksByCategoryId(categoryId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: skillTasksByCategoryIdQuery,
      variables: { categoryId, limit: 1000, nextToken: token },
    });
    const data = result.data.skillTasksByCategoryId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch all sub-categories for a company, handling pagination. */
export async function subCategoriesByCompanyId(companyId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: subCategoriesByCompanyIdQuery,
      variables: { companyId, limit: 1000, nextToken: token },
    });
    const data = result.data.subCategoriesByCompanyId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch sub-categories by category ID, handling pagination. */
export async function subCategoriesByCategoryId(categoryId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: subCategoriesByCategoryIdQuery,
      variables: { categoryId, limit: 1000, nextToken: token },
    });
    const data = result.data.subCategoriesByCategoryId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch all skill levels for a company, handling pagination. */
export async function skillLevelsByCompanyId(companyId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: skillLevelsByCompanyIdQuery,
      variables: { companyId, limit: 1000, nextToken: token },
    });
    const data = result.data.skillLevelsByCompanyId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch skill assignments for a user, handling pagination. */
export async function skillAssignmentsByUserId(userId: string) {
  return paginateSkillAssignments(
    skillAssignmentsByUserIdQuery,
    skillAssignmentsByUserIdQueryLegacy,
    { userId },
    'skillAssignmentsByUserId',
  );
}

/** Fetch all skill assignments for a company, handling pagination. */
export async function skillAssignmentsByCompanyId(companyId: string) {
  return paginateSkillAssignments(
    skillAssignmentsByCompanyIdQuery,
    skillAssignmentsByCompanyIdQueryLegacy,
    { companyId },
    'skillAssignmentsByCompanyId',
  );
}

async function paginateSkillAssignments(
  query: string,
  legacyQuery: string,
  baseVariables: Record<string, string>,
  dataKey: string,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;
  let activeQuery =
    skillAssignmentHistorySupported === false ? legacyQuery : query;
  let triedLegacy = activeQuery === legacyQuery;

  do {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any = await authClient.graphql({
        query: activeQuery,
        variables: { ...baseVariables, limit: 1000, nextToken: token },
      });
      const data = result.data[dataKey];
      allItems.push(...(data.items ?? []));
      token = data.nextToken;
      if (activeQuery === query) skillAssignmentHistorySupported = true;
    } catch (error) {
      if (!triedLegacy && activeQuery === query) {
        triedLegacy = true;
        skillAssignmentHistorySupported = false;
        activeQuery = legacyQuery;
        token = undefined;
        allItems.length = 0;
        continue;
      }
      throw error;
    }
  } while (token);

  return allItems;
}

/** Fetch user files for a user, handling pagination. */
export async function userFilesByUserId(userId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: userFilesByUserIdQuery,
      variables: { userId, limit: 1000, nextToken: token },
    });
    const data = result.data.userFilesByUserId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch all user files for a company, handling pagination. */
export async function userFilesByCompanyId(companyId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: userFilesByCompanyIdQuery,
      variables: { companyId, limit: 1000, nextToken: token },
    });
    const data = result.data.userFilesByCompanyId;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

/** Fetch change log entries for a user, sorted by createdAt descending. */
export async function changeLogEntriesByUserId(userId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems: any[] = [];
  let token: string | null | undefined = undefined;

  do {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await authClient.graphql({
      query: changeLogEntriesByUserIdQuery,
      variables: { userId, sortDirection: ModelSortDirection.DESC, limit: 1000, nextToken: token },
    });
    const data = result.data.changeLogEntriesByUserIdAndCreatedAt;
    allItems.push(...(data.items ?? []));
    token = data.nextToken;
  } while (token);

  return allItems;
}

// ---------------------------------------------------------------------------
// Mutation helpers — Classification
// ---------------------------------------------------------------------------

/** Create a new Classification record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createClassification(input: { companyId: string; name: string }): Promise<any> {
  const result = await authClient.graphql({
    query: createClassificationMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.createClassification;
}

/** Update an existing Classification record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateClassification(input: { id: string; name?: string }): Promise<any> {
  const result = await authClient.graphql({
    query: updateClassificationMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.updateClassification;
}

/** Delete a Classification record by ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function deleteClassification(id: string): Promise<any> {
  const result = await authClient.graphql({
    query: deleteClassificationMutation,
    variables: { input: { id } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.deleteClassification;
}

// ---------------------------------------------------------------------------
// Mutation helpers — SkillCategory
// ---------------------------------------------------------------------------

/** Create a new SkillCategory record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createSkillCategory(input: { classificationId: string; companyId: string; name: string; allowMachineNumber: boolean; equipmentCategoryId?: string | null }): Promise<any> {
  const result = await authClient.graphql({
    query: createSkillCategoryMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.createSkillCategory;
}

/** Update an existing SkillCategory record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateSkillCategory(input: { id: string; name?: string; allowMachineNumber?: boolean; equipmentCategoryId?: string | null }): Promise<any> {
  const result = await authClient.graphql({
    query: updateSkillCategoryMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.updateSkillCategory;
}

/** Delete a SkillCategory record by ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function deleteSkillCategory(id: string): Promise<any> {
  const result = await authClient.graphql({
    query: deleteSkillCategoryMutation,
    variables: { input: { id } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.deleteSkillCategory;
}

// ---------------------------------------------------------------------------
// Mutation helpers — SkillTask
// ---------------------------------------------------------------------------

/** Create a new SkillTask record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createSkillTask(input: { categoryId: string; companyId: string; name: string }): Promise<any> {
  const result = await authClient.graphql({
    query: createSkillTaskMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.createSkillTask;
}

/** Update an existing SkillTask record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateSkillTask(input: { id: string; name?: string }): Promise<any> {
  const result = await authClient.graphql({
    query: updateSkillTaskMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.updateSkillTask;
}

/** Delete a SkillTask record by ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function deleteSkillTask(id: string): Promise<any> {
  const result = await authClient.graphql({
    query: deleteSkillTaskMutation,
    variables: { input: { id } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.deleteSkillTask;
}

// ---------------------------------------------------------------------------
// Mutation helpers — SubCategory
// ---------------------------------------------------------------------------

/** Create a new SubCategory record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createSubCategory(input: { categoryId: string; companyId: string; name: string }): Promise<any> {
  const result = await authClient.graphql({
    query: createSubCategoryMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.createSubCategory;
}

/** Update an existing SubCategory record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateSubCategory(input: { id: string; name?: string }): Promise<any> {
  const result = await authClient.graphql({
    query: updateSubCategoryMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.updateSubCategory;
}

/** Delete a SubCategory record by ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function deleteSubCategory(id: string): Promise<any> {
  const result = await authClient.graphql({
    query: deleteSubCategoryMutation,
    variables: { input: { id } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.deleteSubCategory;
}

// ---------------------------------------------------------------------------
// Mutation helpers — SkillLevel
// ---------------------------------------------------------------------------

/** Create a new SkillLevel record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createSkillLevel(input: { companyId: string; name: string; description?: string; color?: string; sortOrder: number }): Promise<any> {
  const result = await authClient.graphql({
    query: createSkillLevelMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.createSkillLevel;
}

/** Update an existing SkillLevel record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateSkillLevel(input: { id: string; name?: string; description?: string; color?: string; sortOrder?: number }): Promise<any> {
  const result = await authClient.graphql({
    query: updateSkillLevelMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.updateSkillLevel;
}

/** Delete a SkillLevel record by ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function deleteSkillLevel(id: string): Promise<any> {
  const result = await authClient.graphql({
    query: deleteSkillLevelMutation,
    variables: { input: { id } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.deleteSkillLevel;
}

// ---------------------------------------------------------------------------
// Mutation helpers — SkillAssignment
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Mutation helpers — SkillAssignment
// ---------------------------------------------------------------------------

/** Create a new SkillAssignment record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createSkillAssignment(input: {
  userId: string;
  taskId: string;
  companyId: string;
  subCategoryId?: string | null;
  skillLevel: string;
  evaluatedBy?: string | null;
  evaluatedByName?: string | null;
  evaluationDate?: string | null;
  evaluationHistory?: string | null;
}): Promise<any> {
  const { evaluationHistory, ...baseInput } = input;
  const tryHistory =
    evaluationHistory != null && skillAssignmentHistorySupported !== false;

  try {
    const result = await authClient.graphql({
      query: createSkillAssignmentMutation,
      variables: { input: tryHistory ? input : baseInput },
    });
    if (tryHistory) skillAssignmentHistorySupported = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (result as any).data.createSkillAssignment;
  } catch (error) {
    if (!tryHistory) throw error;
    skillAssignmentHistorySupported = false;
    const result = await authClient.graphql({
      query: createSkillAssignmentMutation,
      variables: { input: baseInput },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (result as any).data.createSkillAssignment;
  }
}

/** Update an existing SkillAssignment record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateSkillAssignment(input: {
  id: string;
  subCategoryId?: string | null;
  skillLevel?: string;
  evaluatedBy?: string | null;
  evaluatedByName?: string | null;
  evaluationDate?: string | null;
  evaluationHistory?: string | null;
}): Promise<any> {
  const { evaluationHistory, ...baseInput } = input;
  const tryHistory =
    evaluationHistory != null && skillAssignmentHistorySupported !== false;

  try {
    const result = await authClient.graphql({
      query: updateSkillAssignmentMutation,
      variables: { input: tryHistory ? input : baseInput },
    });
    if (tryHistory) skillAssignmentHistorySupported = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (result as any).data.updateSkillAssignment;
  } catch (error) {
    if (!tryHistory) throw error;
    skillAssignmentHistorySupported = false;
    const result = await authClient.graphql({
      query: updateSkillAssignmentMutation,
      variables: { input: baseInput },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (result as any).data.updateSkillAssignment;
  }
}

/** Delete a SkillAssignment record by ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function deleteSkillAssignment(id: string): Promise<any> {
  const result = await authClient.graphql({
    query: deleteSkillAssignmentMutation,
    variables: { input: { id } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.deleteSkillAssignment;
}

// ---------------------------------------------------------------------------
// Mutation helpers — UserFile
// ---------------------------------------------------------------------------

/** Create a new UserFile record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createUserFile(input: {
  userId: string;
  companyId: string;
  fileName: string;
  s3Path: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: string;
}): Promise<any> {
  const result = await authClient.graphql({
    query: createUserFileMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.createUserFile;
}

/** Delete a UserFile record by ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function deleteUserFile(id: string): Promise<any> {
  const result = await authClient.graphql({
    query: deleteUserFileMutation,
    variables: { input: { id } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.deleteUserFile;
}

/** Update a UserFile record (e.g. rename). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateUserFile(input: { id: string; fileName?: string }): Promise<any> {
  const result = await authClient.graphql({
    query: updateUserFileMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.updateUserFile;
}

// ---------------------------------------------------------------------------
// Mutation helpers — ChangeLogEntry
// ---------------------------------------------------------------------------

/** Create a new ChangeLogEntry record. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createChangeLogEntry(input: {
  userId: string;
  companyId: string;
  fieldName: string;
  oldValue?: string | null;
  newValue?: string | null;
  description: string;
  changedBy: string;
  changedByName: string;
}): Promise<any> {
  const result = await authClient.graphql({
    query: createChangeLogEntryMutation,
    variables: { input },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.createChangeLogEntry;
}
